/**
 * Reusable form components with built-in validation
 */

import React from 'react'
import { ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'

interface BaseFieldProps {
  label: string
  name: string
  error?: string
  warning?: string
  success?: string
  required?: boolean
  disabled?: boolean
  className?: string
  helpText?: string
}

interface ValidatedInputProps extends BaseFieldProps {
  type?: 'text' | 'email' | 'password' | 'number'
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  maxLength?: number
  min?: number
  max?: number
  step?: number
}

interface ValidatedSelectProps extends BaseFieldProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  options: { value: string; label: string; disabled?: boolean }[]
  placeholder?: string
}

interface ValidationSummaryProps {
  errors: Record<string, string>
  warnings?: Record<string, string>
  title?: string
  className?: string
}

/**
 * Get field styling based on validation state
 */
const getFieldClasses = (error?: string, warning?: string, success?: string, baseClasses = '') => {
  let classes = baseClasses || 'w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent'
  
  if (error) {
    classes += ' border-red-300 dark:border-red-600 focus:ring-red-500'
  } else if (warning) {
    classes += ' border-yellow-300 dark:border-yellow-600 focus:ring-yellow-500'
  } else if (success) {
    classes += ' border-green-300 dark:border-green-600 focus:ring-green-500'
  } else {
    classes += ' border-gray-300 dark:border-gray-600'
  }
  
  return classes
}

/**
 * Validation feedback component
 */
const ValidationFeedback: React.FC<{ error?: string; warning?: string; success?: string }> = ({
  error,
  warning,
  success
}) => {
  if (error) {
    return (
      <div className="mt-1 flex items-center text-sm text-red-600 dark:text-red-400">
        <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
        {error}
      </div>
    )
  }
  
  if (warning) {
    return (
      <div className="mt-1 flex items-center text-sm text-yellow-600 dark:text-yellow-400">
        <ExclamationTriangleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
        {warning}
      </div>
    )
  }
  
  if (success) {
    return (
      <div className="mt-1 flex items-center text-sm text-green-600 dark:text-green-400">
        <CheckCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" />
        {success}
      </div>
    )
  }
  
  return null
}

/**
 * Field label component
 */
const FieldLabel: React.FC<{ label: string; name: string; required?: boolean; helpText?: string }> = ({
  label,
  name,
  required,
  helpText
}) => (
  <div className="mb-2">
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {helpText && (
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{helpText}</p>
    )}
  </div>
)

/**
 * Validated input field
 */
export const ValidatedInput: React.FC<ValidatedInputProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  error,
  warning,
  success,
  required,
  disabled,
  className,
  helpText,
  placeholder,
  maxLength,
  min,
  max,
  step
}) => (
  <div className={className}>
    <FieldLabel label={label} name={name} required={required} helpText={helpText} />
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={(e) => { onChange(e.target.value) }}
      onBlur={onBlur}
      className={getFieldClasses(error, warning, success)}
      placeholder={placeholder}
      maxLength={maxLength}
      min={min}
      max={max}
      step={step}
      required={required}
      disabled={disabled}
    />
    <ValidationFeedback error={error} warning={warning} success={success} />
  </div>
)


/**
 * Validated select field
 */
export const ValidatedSelect: React.FC<ValidatedSelectProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  warning,
  success,
  required,
  disabled,
  className,
  helpText,
  options,
  placeholder
}) => (
  <div className={className}>
    <FieldLabel label={label} name={name} required={required} helpText={helpText} />
    <select
      id={name}
      name={name}
      value={value}
      onChange={(e) => { onChange(e.target.value) }}
      onBlur={onBlur}
      className={getFieldClasses(error, warning, success)}
      required={required}
      disabled={disabled}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
    <ValidationFeedback error={error} warning={warning} success={success} />
  </div>
)


/**
 * Validation summary component
 */
export const ValidationSummary: React.FC<ValidationSummaryProps> = ({
  errors,
  warnings = {},
  title = "Please fix the following validation errors:",
  className
}) => {
  const hasErrors = Object.keys(errors).length > 0
  const hasWarnings = Object.keys(warnings).length > 0
  
  if (!hasErrors && !hasWarnings) return null

  return (
    <div className={`rounded-lg p-4 ${className}`}>
      {hasErrors && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-red-800 dark:text-red-200">
              <p className="font-medium mb-2">{title}</p>
              <ul className="list-disc list-inside space-y-1">
                {Object.entries(errors).map(([field, error]) => (
                  <li key={field}>
                    <span className="font-medium">{field}:</span> {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {hasWarnings && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium mb-2">Warnings:</p>
              <ul className="list-disc list-inside space-y-1">
                {Object.entries(warnings).map(([field, warning]) => (
                  <li key={field}>
                    <span className="font-medium">{field}:</span> {warning}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Form section component
 */
export const FormSection: React.FC<{
  title: string
  description?: string
  children: React.ReactNode
  className?: string
  collapsible?: boolean
  defaultExpanded?: boolean
}> = ({
  title,
  description,
  children,
  className,
  collapsible = false,
  defaultExpanded = true
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded)

  return (
    <div className={`bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {description}
            </p>
          )}
        </div>
        {collapsible && (
          <button
            type="button"
            onClick={() => { setIsExpanded(!isExpanded) }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            {isExpanded ? '−' : '+'}
          </button>
        )}
      </div>
      {(!collapsible || isExpanded) && children}
    </div>
  )
}

/**
 * Form actions component
 */
export const FormActions: React.FC<{
  onCancel?: () => void
  onSubmit?: () => void
  submitLabel?: string
  cancelLabel?: string
  isSubmitting?: boolean
  isValid?: boolean
  className?: string
}> = ({
  onCancel,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  isValid = true,
  className
}) => (
  <div className={`flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 ${className}`}>
    {onCancel && (
      <button
        type="button"
        onClick={onCancel}
        disabled={isSubmitting}
        className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {cancelLabel}
      </button>
    )}
    <button
      type="submit"
      onClick={onSubmit}
      disabled={isSubmitting || !isValid}
      className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {isSubmitting ? 'Saving...' : submitLabel}
    </button>
  </div>
)
